import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { type TFunction } from "i18next";

import { useIsDemoExperience } from "@av/apps/common-apps/platform/services/demo-experience/demo";
import { toApplicationCloseDate } from "@av/apps/recruitment/ats-common/application-close-date";
import { buildJobPostDescriptionSearchIndex } from "@av/apps/recruitment/ats-common/job-post-description-search";
import { getLocalizedJobPostContent } from "@av/apps/recruitment/ats-common/localized-job-post-content";
import { useMockDbStore } from "@av/apps/recruitment/ats-common/mock-db/mock-db-store";
import { jobsQueryKeys } from "@av/apps/recruitment/services/recruitment-job-posts/recruitment-jobs-query-key-factory";
import { bffAxios } from "@av/config/api-config/axios-config";
import { dayjs } from "@av/config/extended-dayjs";
import { useLocaleStore } from "@av/utils/hooks/useLocaleStore";

import type {
  EmploymentType,
  JobPostDataUI,
  JobPostFilters,
  JobPostModel,
  JobPostStatus,
  LocationType,
  MockJobPostData,
} from "../../types";

type FetchJobPostsOptions = {
  enabled?: boolean;
};

export const useFetchJobPosts = (
  jobPostFilters: JobPostFilters,
  options?: FetchJobPostsOptions
) => {
  const { t } = useTranslation();
  const { locale } = useLocaleStore();

  const isDemoExperience = useIsDemoExperience();
  const mockedJobPosts = useMockDbStore((state) => state.jobPosts);
  const { searchText = "" } = jobPostFilters;
  const normalizedSearchText = searchText.toLowerCase().trimEnd();
  const hasSearchText = normalizedSearchText !== "";
  const hasCompensationFilter = (jobPostFilters.compensation ?? 0) > 0;

  const query = useQuery({
    enabled: options?.enabled ?? true,
    queryKey: jobsQueryKeys.list({}),
    queryFn: async () => {
      if (!isDemoExperience) {
        const { data: jobPosts } = await bffAxios.get<{ items: JobPostModel[] }>("/job-posts");

        return jobPosts.items as Array<Partial<JobPostModel>>;
      }

      return mockedJobPosts;
    },
  });

  // Gated on a boolean so the index is built once when a search starts, not on every keystroke.
  const descriptionSearchIndex = useMemo(
    () =>
      hasSearchText
        ? buildJobPostDescriptionSearchIndex(query.data ?? [], locale)
        : new Map<string, string>(),
    [query.data, locale, hasSearchText]
  );

  const mappedJobPosts = useMemo(
    () =>
      (query.data ?? []).map((job): JobPostDataUI => {
        const typedJob = job as Partial<JobPostModel & MockJobPostData>;

        return {
          ...(typedJob as MockJobPostData),
          recruiters: typedJob.recruiters ?? [],
          hiringManagers: typedJob.hiringManagers ?? [],
          subdivisionCode: typedJob.subdivisionCode ?? null,
          postedDate: typedJob.postedDate ? dayjs(typedJob.postedDate) : undefined,
          closedDate: typedJob.closedDate ? dayjs(typedJob.closedDate) : undefined,
          archivedDate: typedJob.archivedDate ? dayjs(typedJob.archivedDate) : undefined,
          applyBy: typedJob.applyBy ? toApplicationCloseDate(dayjs(typedJob.applyBy)) : undefined,
        };
      }),
    [query.data]
  );

  // Filtering can be moved to the backend
  const filteredJobPosts = useMemo(() => {
    const filtered = mappedJobPosts.filter(
      (job) =>
        (!jobPostFilters.startDate ||
          !job.postedDate ||
          job.postedDate.isSameOrAfter(jobPostFilters.startDate, "day")) &&
        (!job.postedDate ||
          job.postedDate.isSameOrBefore(jobPostFilters.endDate, "day") ||
          !jobPostFilters.endDate) &&
        (!jobPostFilters.statuses ||
          jobPostFilters.statuses?.length === 0 ||
          jobPostFilters.statuses?.includes(job.postStatus)) &&
        (!jobPostFilters.excludedStatuses ||
          jobPostFilters.excludedStatuses.length === 0 ||
          !jobPostFilters.excludedStatuses.includes(job.postStatus)) &&
        (!jobPostFilters.jobPostId || jobPostFilters.jobPostId === job.jobPostId) &&
        (!jobPostFilters.city ||
          jobPostFilters.city.length === 0 ||
          jobPostFilters.city?.some(
            (city) => city.trim().toLowerCase() === job.city?.trim().toLowerCase()
          )) &&
        (!jobPostFilters.locationType || job.locationType === jobPostFilters.locationType) &&
        (!hasCompensationFilter ||
          ((job.minCompensation ?? 0) >= jobPostFilters.compensation! &&
            jobPostFilters.compensation! <= (job.maxCompensation ?? Number.MAX_SAFE_INTEGER))) &&
        (normalizedSearchText === "" ||
          getLocalizedJobPostContent(job.jobTitles, locale)
            .toLocaleLowerCase()
            .includes(normalizedSearchText) ||
          descriptionSearchIndex.get(job.jobPostId ?? "")?.includes(normalizedSearchText) ||
          job.postedDate
            ?.format(t("common.components.dateFormat"))
            .toLocaleLowerCase()
            .includes(normalizedSearchText) ||
          job.applyBy
            ?.format(t("common.components.dateFormat"))
            .toLocaleLowerCase()
            .includes(normalizedSearchText) ||
          job.closedDate
            ?.format(t("common.components.dateFormat"))
            .toLocaleLowerCase()
            .includes(normalizedSearchText) ||
          String(job.postStatus)?.toLocaleLowerCase().includes(normalizedSearchText) ||
          job.city?.toLocaleLowerCase().includes(normalizedSearchText) ||
          String(job.jobId).includes(normalizedSearchText))
    );

    filtered.sort((jobA, jobB) => {
      const a = jobA.postedDate?.valueOf() ?? 0;
      const b = jobB.postedDate?.valueOf() ?? 0;

      return b - a;
    });

    return filtered;
  }, [
    mappedJobPosts,
    jobPostFilters,
    hasCompensationFilter,
    normalizedSearchText,
    locale,
    t,
    descriptionSearchIndex,
  ]);

  return { ...query, data: filteredJobPosts };
};

export const useFetchArchivedJobPosts = () => useFetchJobPosts({ statuses: ["ARCHIVED"] });

export const useFetchSingleJobPost = (jobPostId?: string) => {
  const isDemoExperience = useIsDemoExperience();
  const jobPosts = useMockDbStore((state) => state.jobPosts);

  const { data: jobPost = [], isLoading: isLoadingJobPost } = useFetchJobPosts({
    jobPostId: jobPostId,
  });

  const query = useQuery<JobPostDataUI>({
    queryKey: jobsQueryKeys.single(jobPostId),
    enabled: isDemoExperience ? !!jobPostId : !!jobPostId && !isLoadingJobPost,
    queryFn: async (): Promise<JobPostDataUI> => {
      if (isDemoExperience) {
        const mockJobPost = jobPosts.find((j) => j.jobPostId === jobPostId);

        if (mockJobPost) {
          const typedJobPost = mockJobPost as Partial<JobPostModel & MockJobPostData>;

          return {
            ...(typedJobPost as MockJobPostData),
            recruiters: typedJobPost.recruiters ?? [],
            hiringManagers: typedJobPost.hiringManagers ?? [],
            postedDate: typedJobPost.postedDate ? dayjs(typedJobPost.postedDate) : undefined,
            closedDate: typedJobPost.closedDate ? dayjs(typedJobPost.closedDate) : undefined,
            archivedDate: typedJobPost.archivedDate ? dayjs(typedJobPost.archivedDate) : undefined,
            applyBy: typedJobPost.applyBy
              ? toApplicationCloseDate(dayjs(typedJobPost.applyBy))
              : undefined,
          } as JobPostDataUI;
        }

        return {} as JobPostDataUI;
      }

      return jobPost.length > 0 ? jobPost[0] : ({} as JobPostDataUI);
    },
    staleTime: 0,
  });

  return { ...query, isLoading: query.isLoading || isLoadingJobPost };
};

export function resolveLocationTypeTranslation(t: TFunction, locationType?: LocationType): string {
  if (!locationType) return "-";

  const translationMap: Record<LocationType, string> = {
    REMOTE: t("recruitment.common.locationTypeEnum_remote"),
    HYBRID: t("recruitment.common.locationTypeEnum_hybrid"),
    IN_PERSON: t("recruitment.common.locationTypeEnum_inPerson"),
  };

  return translationMap[locationType] ?? "-";
}

export function resolveEmploymentTypeTranslation(
  t: TFunction,
  employmentType?: EmploymentType
): string {
  if (!employmentType) return "-";

  const translationMap: Record<EmploymentType, string> = {
    FULL_TIME: t("recruitment.common.employmentTypeEnum_fullTime"),
    PART_TIME: t("recruitment.common.employmentTypeEnum_partTime"),
    CO_OP: t("recruitment.common.employmentTypeEnum_coOp"),
    FIXED_TERM: t("recruitment.common.employmentTypeEnum_fixedTerm"),
  };

  return translationMap[employmentType] ?? "-";
}

export function statusToTranslations(
  status: JobPostStatus | undefined,
  t: TFunction<"translation", undefined>
): string {
  switch (status) {
    case "DRAFT":
      return t("recruitment.jobs.statusEnum_draft");
    case "POSTED":
      return t("recruitment.jobs.statusEnum_posted");
    case "CLOSED":
      return t("recruitment.jobs.statusEnum_closed");
    case "ARCHIVED":
      return t("recruitment.jobs.statusEnum_archived");
    default:
      return status ?? "";
  }
}
